"use client";

import clsx from "clsx";
import React, {useLayoutEffect, useRef} from "react";
import {useTranslation} from "react-i18next";
import Button from "@/components/library/Button";
import {useGailDock} from "@/contexts/chat/GailDockContext";
import {useResizablePanel} from "@/hooks/useResizablePanel";
import useScreenSize from "@/hooks/useScreenSize";
import GailDockPanel from "./GailDockPanel";

const MIN_DOCK_WIDTH = 360;
const MAX_DOCK_VW = 0.6;
const DEFAULT_DOCK_WIDTH = 416;
const MIN_PAGE_WIDTH = 480;

function pageFrameClass(isTakeover: boolean, dockOnRight: boolean): string {
   if (!isTakeover) return "grid";
   if (dockOnRight) return "h-full min-h-0 p-2 pr-0";
   return "h-full min-h-0 p-2 pl-0";
}

/** Keep the page's scroll position when the dock switches between overlay and takeover. */
function useSyncedPageScroll(isTakeover: boolean) {
   const pageScrollerRef = useRef<HTMLDivElement>(null);
   const lastWindowScrollY = useRef(0);
   const lastCardScrollTop = useRef(0);
   const prevTakeover = useRef(isTakeover);

   useLayoutEffect(() => {
      const onWindowScroll = () => {
         lastWindowScrollY.current = window.scrollY;
      };
      window.addEventListener("scroll", onWindowScroll, {passive: true});
      return () => window.removeEventListener("scroll", onWindowScroll);
   }, []);

   useLayoutEffect(() => {
      if (prevTakeover.current === isTakeover) return;
      prevTakeover.current = isTakeover;
      if (isTakeover) {
         const scroller = pageScrollerRef.current;
         if (scroller) scroller.scrollTop = lastWindowScrollY.current;
      } else {
         window.scrollTo(0, lastCardScrollTop.current);
      }
   }, [isTakeover]);

   return {
      pageScrollerRef,
      rememberPageScroll: (scrollTop: number) => {
         lastCardScrollTop.current = scrollTop;
      },
   };
}

const GailDockLayout: React.FC<{children: React.ReactNode}> = ({children}) => {
   const {t} = useTranslation("ask");
   const {isDockAvailable, isOpen, side} = useGailDock();
   const {isMobile} = useScreenSize();
   const showDock = isDockAvailable && isOpen;
   const isTakeover = showDock && !isMobile;
   const dockOnRight = side === "right";

   const {width: dockWidth, maxWidth, isDragging, handleMouseDown, handleKeyDown} =
      useResizablePanel({
         minWidth: MIN_DOCK_WIDTH,
         maxViewportFraction: MAX_DOCK_VW,
         defaultWidth: DEFAULT_DOCK_WIDTH,
         minCounterpartWidth: MIN_PAGE_WIDTH,
         anchor: dockOnRight ? "right" : "left",
      });

   const {pageScrollerRef, rememberPageScroll} = useSyncedPageScroll(isTakeover);

   const dockColumn = !isMobile && (
      <aside
         key="gail-dock"
         aria-hidden={!showDock}
         style={{width: showDock ? `${dockWidth}px` : 0}}
         className={clsx(
            "flex-shrink-0 overflow-hidden",
            !isDragging && "transition-[width] duration-200 ease-out"
         )}
      >
         <div style={{width: `${dockWidth}px`}} className="sticky top-0 h-screen">
            {showDock && <GailDockPanel />}
         </div>
      </aside>
   );

   const resizeHandle = isTakeover && (
      <Button
         key="gail-dock-resize"
         type="none"
         onClick={() => {}}
         role="slider"
         aria-orientation="vertical"
         aria-label={t("dock.resize")}
         aria-valuemin={MIN_DOCK_WIDTH}
         aria-valuemax={Math.round(maxWidth)}
         aria-valuenow={Math.round(dockWidth)}
         onMouseDown={handleMouseDown}
         onKeyDown={handleKeyDown}
         className="group flex-shrink-0 w-1 cursor-col-resize relative !p-0 !m-0 !border-0 bg-transparent focus:outline-none"
      >
         <span
            aria-hidden="true"
            className={clsx(
               "absolute inset-x-0 inset-y-5 rounded-full transition-colors",
               "group-hover:bg-primary/30",
               "group-focus-visible:bg-primary/50 group-focus-visible:ring-2 group-focus-visible:ring-primary/40",
               isDragging && "bg-primary/50"
            )}
         />
         <span className="absolute inset-y-0 -left-1 -right-1" aria-hidden="true" />
      </Button>
   );

   const pageColumn = (
      <span
         key="gail-dock-page"
         className={clsx("flex-1 min-w-0", pageFrameClass(isTakeover, dockOnRight))}
      >
         <div
            className={clsx(
               isTakeover
                  ? "h-full overflow-hidden rounded-xl border border-border shadow-lg bg-[#f9f9f9] dark:bg-[#252930]"
                  : "grid min-w-0 grid-cols-[minmax(0,1fr)]"
            )}
         >
            <span
               ref={pageScrollerRef}
               onScroll={(e) => {
                  rememberPageScroll(e.currentTarget.scrollTop);
               }}
               className={clsx(
                  isTakeover
                     ? "block h-full overflow-auto"
                     : "grid min-w-0 grid-cols-[minmax(0,1fr)] grid-rows-[1fr]"
               )}
            >
               {children}
            </span>
         </div>
      </span>
   );

   return (
      <span
         className={clsx(
            "flex w-full",
            isTakeover
               ? "h-screen overflow-hidden bg-white dark:bg-[#2d323a] transition-colors duration-200"
               : "min-h-[calc(100dvh-4rem)] md:min-h-dvh"
         )}
      >
         {dockOnRight ? pageColumn : dockColumn}
         {resizeHandle}
         {dockOnRight ? dockColumn : pageColumn}
         {isMobile && showDock && (
            <div className="fixed inset-0 z-40 bg-surface">
               <GailDockPanel />
            </div>
         )}
      </span>
   );
};

export default GailDockLayout;
